import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Section } from "@/components/common";

const AUDIENCES = [["resident", "Local resident / citizen"], ["customer", "Data center customer"], ["operator", "Operator or hyperscaler"], ["developer", "Developer or landowner"], ["utility", "Utility / infrastructure"], ["public_official", "Public official"], ["journalist", "Journalist or researcher"], ["other", "Other"]];
const TOPICS = ["Water", "Power", "Cooling", "Jobs", "Tax Revenue", "Emissions", "Noise", "Community", "Policy", "International"];

export default function Preferences() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [sub, setSub] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!token) { setErr(true); return; }
    api.get(`/newsletter/preferences/${token}`).then((r) => setSub(r.data)).catch(() => setErr(true));
  }, [token]);

  const toggle = (t) => setSub((s) => ({ ...s, topics: (s.topics || []).includes(t) ? s.topics.filter((x) => x !== t) : [...(s.topics || []), t] }));

  const save = async () => {
    try {
      await api.post("/newsletter/preferences", { token, state: sub.state, topics: sub.topics, audience_type: sub.audience_type });
      toast.success("Preferences updated.");
    } catch { toast.error("Could not save."); }
  };

  if (err) return <Section className="py-24 max-w-lg text-center"><div className="card p-10"><h1 className="font-display font-bold text-2xl text-white">Invalid link</h1><p className="text-slate-400 mt-2">This preferences link is not valid.</p></div></Section>;
  if (!sub) return <Section className="py-24"><p className="text-slate-400 font-mono">Loading…</p></Section>;

  return (
    <Section className="py-16 max-w-lg">
      <p className="eyebrow mb-2">Manage subscription</p>
      <h1 className="font-display font-black text-3xl text-white mb-1">Your preferences</h1>
      <p className="text-slate-400 text-sm mb-6">{sub.email}</p>
      <div className="card p-6 sm:p-8 space-y-5" data-testid="preferences-form">
        <div>
          <label className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Audience type</label>
          <select data-testid="pref-audience" value={sub.audience_type} onChange={(e) => setSub({ ...sub, audience_type: e.target.value })}
            className="w-full mt-2 bg-[#0B0F17] border border-white/10 rounded-lg px-3 py-3 text-white focus:outline-none focus:border-blue-500">
            {AUDIENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="font-mono text-[11px] uppercase tracking-widest text-slate-400">State or region</label>
          <input data-testid="pref-state" value={sub.state || ""} onChange={(e) => setSub({ ...sub, state: e.target.value.toUpperCase() })}
            className="w-full mt-2 bg-[#0B0F17] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-blue-500" />
        </div>
        <div>
          <label className="font-mono text-[11px] uppercase tracking-widest text-slate-400">Topics</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {TOPICS.map((t) => (
              <button key={t} onClick={() => toggle(t)} data-testid={`pref-topic-${t}`}
                className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${(sub.topics || []).includes(t) ? "bg-[#2563EB] border-[#2563EB] text-white" : "border-white/15 text-slate-300 hover:border-blue-500/50"}`}>{t}</button>
            ))}
          </div>
        </div>
        <button data-testid="pref-save" onClick={save} className="w-full bg-[#84CC16] hover:bg-[#9ee325] text-[#0B0F17] font-bold py-3 rounded-lg transition-colors">Save preferences</button>
      </div>
    </Section>
  );
}
